import { Logout } from '@mui/icons-material';
import { Avatar, Box, ClickAwayListener, Popper, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { userDataDefaultValues, useUserDataContext } from '../../provider/UserDataProvider';
import apiService from '../../utils/apiService';
import { immersightBlue, immersightGrey, logoutBackground, logoutText } from '../../utils/colors';
import { parseUserDataJsonObject } from '../../utils/jsonParser';

// --- Imports for Region Switching Logic ---
import PlanModel from '../../models/standard/PlanModel';
import { useAnnotations } from '../../provider/AnnotationsProvider';
import { useBuilding } from '../../provider/BuildingProvider/BuildingProvider';
import { useLoginContext } from '../../provider/LoginProvider';
import { usePlan } from '../../provider/PlanProvider';
import { COUNTRY_TO_REGION, ERegionCode } from '../../utils/regionUtils';

const UserProfile = () => {
  const [displayString, setDisplayString] = useState<string>('');
  const [profileImage, setProfileImage] = useState<string>('');
  const [isDropDownOpen, setIsDropDownOpen] = useState<boolean>(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const { currentUserData, setCurrentUserData } = useUserDataContext();
  const { isAuthenticated, logout } = useLoginContext();

  // --- Contexts for Region Logic ---
  const { building } = useBuilding();
  const { hotspots } = useAnnotations();
  const { setPlanModel } = usePlan();

  const requestFullUserData = async () => {
    if (!currentUserData.authentificationtoken) return;

    // Fetching the user data.
    const userJsonRequestObject = {
      authentificationtoken: currentUserData.authentificationtoken,
    };
    const apiResponse = await apiService.postRequest('user', userJsonRequestObject, currentUserData.authentificationtoken);
    
    if (apiResponse.status === 'ACCESS GRANTED') {
      const userData = parseUserDataJsonObject(apiResponse, currentUserData.authentificationtoken);
      setCurrentUserData(userData);
  
      // Auto-switch Region for Empty Plans ---
      const isPlanEmpty = (!building.walls || building.walls.length === 0) && (!hotspots || hotspots.length === 0);
      if (isPlanEmpty && userData.country) {
        // Map country code (e.g. "US", "DE") to Region Enum
        const targetRegion = COUNTRY_TO_REGION[userData.country] || ERegionCode.DE;
        
        // Only update if different to avoid loops
        setPlanModel((previousPlan) => {
          if (previousPlan.region === targetRegion) {
            return previousPlan;
          }

          console.warn('[PlanDebug] UserProfile applying user region', {
            previousPlan: {
              dataObjectId: previousPlan.dataObjectId,
              eventId: previousPlan.eventId,
              floorplannerId: previousPlan.id,
              kojeId: previousPlan.kojeId,
              kojeLevel: previousPlan.kojeLevel,
              region: previousPlan.region,
              showroomId: previousPlan.showroomId,
            },
            targetRegion,
          });

          return new PlanModel(
            previousPlan.name,
            previousPlan.id,
            previousPlan.kojeId,
            previousPlan.kojeLevel,
            previousPlan.eventId,
            previousPlan.dataObjectId,
            previousPlan.showroomId,
            previousPlan.appVersion,
            previousPlan.building,
            previousPlan.jsonVersion,
            targetRegion,
          );
        });
      }
      // ------------------------------------------------

      setDisplayString(userData.firstName === '' || userData.lastName === '' ? userData.email : `${userData.firstName}\n${userData.lastName}`);
      if (userData.profileImageDataId !== '') {
        // Additionally fetching the profile image resource for the user.
        const profileImageRequestObject = {
          authentificationtoken: userData.authentificationtoken,
          datafile_id: userData.profileImageDataId,
        };
        const responseImageURL = await apiService.imageRequest('datafile', profileImageRequestObject, userData.authentificationtoken);
        setProfileImage(responseImageURL);
      }
    } else if (apiResponse.status === 'ACCESS DENIED' && !isAuthenticated) {
      setCurrentUserData(userDataDefaultValues.currentUserData);
    }
  };

  useEffect(() => {
    setDisplayString(
      currentUserData.firstName === '' || currentUserData.lastName === ''
        ? currentUserData.email
        : `${currentUserData.firstName}\n${currentUserData.lastName}`
    );
  }, [currentUserData.email, currentUserData.firstName, currentUserData.lastName]);

  // Userdata gets fetched once on init.
  useEffect(() => {
    requestFullUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDropDownClick = () => {
    setIsDropDownOpen((prev) => !prev);
  };

  const handleLogout = async () => {
    setCurrentUserData(userDataDefaultValues.currentUserData);
    setIsDropDownOpen(false);
    if (isAuthenticated) {
      await logout();
    }
  };
  
  const handleClose = () => {
    setIsDropDownOpen(false);
  };

  return (
    <ClickAwayListener onClickAway={handleClose}> 
      <Box
        component={'div'}
        onClick={() => console.log(`click on user profile of ${displayString}`)}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
        ref={anchorRef} 
      >
        <Avatar
          onClick={handleDropDownClick}
          alt={displayString}
          src={profileImage}
          sx={{
            width: '3rem',
            height: '3rem',
            boxShadow: '0 0 1px 1px rgba(0, 0, 0, .2)',
            m: '.2rem .5rem',
          }}
        />
        <Typography
          onClick={handleDropDownClick}
          sx={{
            color: immersightGrey,
            fontWeight: '400',
            fontSize: '1rem',
            userSelect: 'none',
          }}
        >
          {displayString}
        </Typography>

        <Popper
          open={isDropDownOpen}
          anchorEl={anchorRef.current}
          placement="bottom-end"
          disablePortal
          sx={{
            zIndex: 1101,
          }}
        >
          <Box
            component={'div'}
            onClick={handleLogout}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              width: 'fit-content',
              bgcolor: logoutBackground,
              zIndex: 1101,
              borderRadius: '.75rem',
              cursor: 'pointer',
              p: '.15rem .6rem .3rem',
              boxShadow: '0 0 2px 2px rgba(0, 0, 0, .2)', 
              '&:active': {
                outline: 'none',
              },
              '&:focus': {
                outline: 'none',
              },
            }}
          >
            <Typography
              sx={{
                ml: '.5rem',
                color: logoutText,
                fontWeight: '400',
                fontSize: '1rem',
                userSelect: 'none',
              }}
            >
              <FormattedMessage id="UserProfile.logout" />
            </Typography>
            <Logout
              sx={{
                width: '1.3rem',
                height: '1.3rem',
                p: '.3rem',
                ml: '.6rem',
                color: immersightBlue,
              }}
            />
          </Box>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default UserProfile;
